import React, { useEffect, useRef, useState } from 'react';
import { useActiveViewportDisplaySets, useSystem } from '@ohif/core';
import { Button, Tooltip, TooltipContent, TooltipTrigger, useImageViewer } from '@ohif/ui-next';
import { AccountRoleEnum } from '../../services/SessionService';
import { useOrganReview } from '../../hooks/useOrganReview';
import ButtonContent from './ButtonContent';
import OrganReviewDialog from '../OrganReviewDialog';

const BASE_CLASS =
  'flex h-10 w-10 items-center justify-center rounded-lg bg-transparent font-medium';

interface ImageViewerContextValue {
  StudyInstanceUIDs: string[];
}

export default function OrganReviewButton({ tooltip }: { tooltip?: string }) {
  const { servicesManager } = useSystem();
  const { sessionService } = servicesManager.services;

  const imageViewer = useImageViewer() as ImageViewerContextValue | null;
  const studyInstanceUid = imageViewer?.StudyInstanceUIDs?.[0];

  const displaySets = useActiveViewportDisplaySets();
  const seriesInstanceUid = displaySets?.[0]?.SeriesInstanceUID;

  const isReviewer = sessionService.getRole() === AccountRoleEnum.REVIEWER;

  const {
    rows,
    hasPending,
    hasError,
    isFullyReviewed,
    isSubmitDisabled,
    setQuality,
    submit,
    refresh,
  } = useOrganReview(
    isReviewer ? studyInstanceUid : undefined,
    isReviewer ? seriesInstanceUid : undefined
  );

  const [open, setOpen] = useState(false);

  const buttonRef = useRef<HTMLButtonElement>(null);

  const closedBySeriesChange = useRef(false);

  const openRef = useRef(false);

  const setPanelOpen = (next: boolean) => {
    openRef.current = next;
    setOpen(next);
  };

  useEffect(() => {
    if (!openRef.current) {
      return;
    }
    closedBySeriesChange.current = true;
    setPanelOpen(false);
  }, [seriesInstanceUid]);

  if (!isReviewer) {
    return null;
  }

  const isDisabled = !studyInstanceUid || !seriesInstanceUid;

  const openPanel = () => {
    void refresh();
    setPanelOpen(true);
  };

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={buttonRef}
            dataCY="sycai-organ-review-button"
            className={`${BASE_CLASS} aria-disabled:cursor-not-allowed aria-disabled:opacity-50`}
            aria-disabled={isDisabled || undefined}
            aria-label="Organ Review"
            onClick={() => !isDisabled && openPanel()}
          >
            <ButtonContent
              hasPending={hasPending}
              isFullyReviewed={isFullyReviewed}
            />
          </Button>
        </TooltipTrigger>
        {tooltip && <TooltipContent side="bottom">{tooltip}</TooltipContent>}
      </Tooltip>

      {seriesInstanceUid && (
        <OrganReviewDialog
          open={open}
          onOpenChange={setPanelOpen}
          rows={rows}
          isSubmitDisabled={isSubmitDisabled}
          isSubmitting={hasPending}
          hasError={hasError}
          returnFocusRef={buttonRef}
          suppressFocusReturn={closedBySeriesChange}
          onQualityChange={setQuality}
          onSubmit={() => void submit()}
        />
      )}
    </>
  );
}
